import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { createCasino, listOwnedCasinos, type OwnedCasinoSummary } from '../lib/casinoApi.js';
import styles from './YourCasinos.module.css';

/**
 * A signed-in account's venues on the join page: each one a tap from its
 * console, and a name field that opens a new one without walking through an
 * existing casino first. The list reads after first paint, so the rows simply
 * appear; a failed read is an empty list and the form still works.
 */
export function YourCasinos() {
  const navigate = useNavigate();
  const [casinos, setCasinos] = useState<OwnedCasinoSummary[]>([]);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    void listOwnedCasinos()
      .catch(() => [])
      .then((rows) => {
        if (live) setCasinos(rows);
      });
    return () => {
      live = false;
    };
  }, []);

  const open = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const created = await createCasino(name.trim());
      // Straight into the new venue's console: its first table is waiting there.
      navigate(`/casino?casino=${encodeURIComponent(created.joinCode)}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create the casino.');
      setBusy(false);
    }
  };

  return (
    <section className={styles.card} aria-labelledby="your-casinos">
      <h2 className={styles.heading} id="your-casinos">
        Your casinos
      </h2>
      {casinos.length > 0 && (
        <ul className={styles.list}>
          {casinos.map((c) => (
            <li key={c.id} className={styles.row}>
              <span className={styles.main}>
                <span className={styles.name}>{c.name}</span>
                <span className={styles.code}>{c.joinCode}</span>
              </span>
              <Link
                className={styles.chip}
                to={`/casino?casino=${encodeURIComponent(c.joinCode)}`}
                aria-label={`Run ${c.name}`}
              >
                Run it
              </Link>
            </li>
          ))}
        </ul>
      )}
      <form className={styles.form} onSubmit={(e) => void open(e)}>
        <input
          className={styles.input}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="New casino name"
          aria-label="New casino name"
          maxLength={60}
        />
        <button className={styles.chip} type="submit" disabled={busy || !name.trim()}>
          {busy ? 'Opening…' : 'Open it'}
        </button>
      </form>
      {error && <p className={styles.error}>{error}</p>}
    </section>
  );
}
